import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode, RefObject } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  PULL_TRIGGER_PX,
  commits,
  damp,
  intentFor,
  lockDirection,
  startZone,
  swipeUpOpens,
} from './swipe.js';
import type { Direction, SwipeIntent } from './swipe.js';

/**
 * Wischgesten (24.10.2026): ein Baustein für die ganze App — Reiter, Zurück,
 * Tage blättern, Zeilen, Ziehen zum Aktualisieren. Die Regeln stehen in
 * `swipe.ts`; hier werden nur Finger gelesen und Elemente verschoben.
 *
 * Gelesen wird mit Touch-Ereignissen statt Pointer-Ereignissen: Safari bricht
 * Pointer ab, sobald es selbst scrollt — dann ginge weder das Ziehen oben noch
 * ein Wischen, das schräg beginnt.
 */

/**
 * Was eine Seite beim Wischen tut (24.10.2026): Sie folgt dem Finger 1:1, die
 * Nachbarseite schiebt sich daneben herein, beim Loslassen läuft die
 * Bewegung zu Ende. So fühlt es sich an wie in einer App — nicht wie
 * „wegrutschen, zurückschnappen, neu laden“.
 */
export interface PaneController {
  /** Gibt es in diese Richtung etwas (1 = nächstes, −1 = vorheriges)? */
  has: (dir: Direction) => boolean;
  /** Fingerstrecke seit Beginn; die Seite entscheidet, was daneben liegt. */
  drag: (dx: number) => void;
  /** Loslassen: zu Ende führen oder zurück. */
  finish: (commit: boolean) => void;
}

interface RowHandlers {
  /** Nach links gewischt (rechte Seite frei) — etwa löschen. */
  left?: () => void;
  /** Nach rechts gewischt (linke Seite frei) — etwa abhaken. */
  right?: () => void;
}

interface SwipeApi {
  registerBack: (handler: () => void) => () => void;
  /** Die Unterreiter einer Seite (`SwipePages`). */
  registerTabs: (controller: PaneController) => () => void;
  /** Ein Bereich mit Tagen oder Zeiträumen. */
  registerPager: (element: HTMLElement, controller: PaneController) => () => void;
  registerRow: (element: HTMLElement, handlers: RowHandlers) => () => void;
  /** Eine Leiste „Rückgängig“ für ein paar Sekunden — nach Löschen per Wischen. */
  showUndo: (message: string, undo: () => void) => void;
}

const SwipeContext = createContext<SwipeApi | null>(null);

const UNDO_MS = 6000;

export function SwipeProvider({
  children,
  contentRef,
  onRefresh,
}: {
  children: ReactNode;
  /** Was beim Wechsel der Unterreiter und beim Zurück mitgeht — der Inhalt. */
  contentRef: RefObject<HTMLElement | null>;
  /** Abgleichen und sagen, wie es ausging. */
  onRefresh: () => Promise<string>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const backs = useRef<(() => void)[]>([]);
  const tabs = useRef<PaneController[]>([]);
  const pagers = useRef(new Map<HTMLElement, PaneController>());
  const rows = useRef(new Map<HTMLElement, RowHandlers>());
  const [pull, setPull] = useState<{
    phase: 'pulling' | 'syncing' | 'done';
    px: number;
    text?: string;
  } | null>(null);
  const [undo, setUndo] = useState<{ message: string; run: () => void; key: number } | null>(null);
  const handlers = useRef({ onRefresh });
  handlers.current = { onRefresh };

  const api = useMemo<SwipeApi>(
    () => ({
      registerBack: (handler) => {
        backs.current.push(handler);
        return () => {
          backs.current = backs.current.filter((entry) => entry !== handler);
        };
      },
      registerTabs: (controller) => {
        tabs.current.push(controller);
        return () => {
          tabs.current = tabs.current.filter((entry) => entry !== controller);
        };
      },
      registerPager: (element, entry) => {
        pagers.current.set(element, entry);
        return () => void pagers.current.delete(element);
      },
      registerRow: (element, entry) => {
        rows.current.set(element, entry);
        return () => void rows.current.delete(element);
      },
      showUndo: (message, run) => setUndo({ message, run, key: Date.now() }),
    }),
    [],
  );

  useEffect(() => {
    if (!undo) return;
    const timer = setTimeout(() => setUndo(null), UNDO_MS);
    return () => clearTimeout(timer);
  }, [undo]);

  const refreshing = useRef(false);
  const startRefresh = useCallback(() => {
    if (refreshing.current) return;
    refreshing.current = true;
    setPull({ phase: 'syncing', px: PULL_TRIGGER_PX });
    void handlers.current
      .onRefresh()
      .catch(() => t('sync.error'))
      .then((text) => {
        setPull({ phase: 'done', px: PULL_TRIGGER_PX, text });
        setTimeout(() => {
          refreshing.current = false;
          setPull(null);
        }, 1800);
      });
  }, [t]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    interface Gesture {
      x: number;
      y: number;
      at: number;
      target: HTMLElement;
      atTop: boolean;
      lock: 'h' | 'v' | null;
      mode: 'none' | 'swipe' | 'pull';
      intent: SwipeIntent | null;
      moved: HTMLElement | null;
      pane: PaneController | null;
      row: HTMLElement | null;
      dx: number;
      dy: number;
    }
    let gesture: Gesture | null = null;

    const owner = <T,>(map: Map<HTMLElement, T>, from: HTMLElement) => {
      for (let node: HTMLElement | null = from; node && node !== root; node = node.parentElement) {
        if (map.has(node)) return node;
      }
      return null;
    };

    const scrollsSideways = (from: HTMLElement) => {
      for (let node: HTMLElement | null = from; node && node !== root; node = node.parentElement) {
        if (node.scrollWidth > node.clientWidth + 1) {
          const overflow = getComputedStyle(node).overflowX;
          if (overflow === 'auto' || overflow === 'scroll') return true;
        }
      }
      return false;
    };

    const move = (element: HTMLElement | null, x: number) => {
      if (!element) return;
      element.style.transition = 'none';
      element.style.transform = x === 0 ? '' : `translateX(${x}px)`;
    };

    const settle = (element: HTMLElement | null) => {
      if (!element) return;
      element.style.transition = reduced ? 'none' : 'transform 180ms ease-out';
      element.style.transform = '';
      const done = () => {
        element.style.transition = '';
        element.removeEventListener('transitionend', done);
      };
      element.addEventListener('transitionend', done);
    };

    const markRow = (row: HTMLElement | null, side: 'left' | 'right' | null) => {
      if (!row) return;
      if (side) row.dataset.swipeSide = side;
      else delete row.dataset.swipeSide;
    };

    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        gesture = null;
        return;
      }
      const target = event.target as HTMLElement;
      // Dialoge haben ihre eigene Geste; in Feldern markiert Wischen Text.
      if (
        target.closest(
          '[role="dialog"], input, textarea, select, [contenteditable], [data-no-swipe]',
        )
      ) {
        gesture = null;
        return;
      }
      const touch = event.touches[0]!;
      gesture = {
        x: touch.clientX,
        y: touch.clientY,
        at: event.timeStamp,
        target,
        atTop: window.scrollY <= 0,
        lock: null,
        mode: 'none',
        intent: null,
        moved: null,
        pane: null,
        row: null,
        dx: 0,
        dy: 0,
      };
    };

    const onMove = (event: TouchEvent) => {
      if (!gesture) return;
      const touch = event.touches[0]!;
      gesture.dx = touch.clientX - gesture.x;
      gesture.dy = touch.clientY - gesture.y;
      if (!gesture.lock) {
        gesture.lock = lockDirection(gesture.dx, gesture.dy);
        if (!gesture.lock) return;
        if (gesture.lock === 'v') {
          if (gesture.atTop && gesture.dy > 0 && !refreshing.current) gesture.mode = 'pull';
          else gesture = null;
          return;
        }
        const width = viewportWidth();
        const row = owner(rows.current, gesture.target);
        const pager = owner(pagers.current, gesture.target);
        const rowHandlers = row ? rows.current.get(row)! : null;
        gesture.intent = intentFor({
          zone: startZone(gesture.x, width),
          towardRight: gesture.dx > 0,
          inSubview: backs.current.length > 0,
          hasTabs: tabs.current.length > 0,
          inPager: pager !== null,
          row: rowHandlers ? { left: !!rowHandlers.left, right: !!rowHandlers.right } : null,
          inScroller: scrollsSideways(gesture.target),
        });
        if (!gesture.intent) {
          gesture = null;
          return;
        }
        gesture.mode = 'swipe';
        gesture.row = gesture.intent.kind === 'row' ? row : null;
        gesture.pane =
          gesture.intent.kind === 'page'
            ? pager
              ? (pagers.current.get(pager) ?? null)
              : null
            : gesture.intent.kind === 'tab'
              ? (tabs.current.at(-1) ?? null)
              : null;
        gesture.moved =
          gesture.intent.kind === 'row'
            ? (row?.querySelector<HTMLElement>('[data-swipe-content]') ?? row)
            : gesture.intent.kind === 'back'
              ? contentRef.current
              : null;
        if (gesture.intent.kind === 'row') markRow(row, gesture.intent.side);
      }
      if (gesture.mode === 'pull') {
        event.preventDefault();
        setPull({ phase: 'pulling', px: damp(gesture.dy) });
        return;
      }
      if (gesture.mode === 'swipe' && gesture.intent) {
        event.preventDefault();
        const intent = gesture.intent;
        // Nur in die erlaubte Richtung — eine Zeile, die nur abhakt, geht nicht nach links.
        const dx =
          intent.kind === 'row'
            ? intent.side === 'right'
              ? Math.max(0, gesture.dx)
              : Math.min(0, gesture.dx)
            : gesture.dx;
        if (gesture.pane) gesture.pane.drag(dx);
        else move(gesture.moved, reduced && intent.kind === 'back' ? 0 : dx);
      }
    };

    const onEnd = (event: TouchEvent) => {
      const current = gesture;
      gesture = null;
      if (!current) return;
      if (current.mode === 'pull') {
        if (damp(current.dy) >= PULL_TRIGGER_PX) startRefresh();
        else setPull(null);
        return;
      }
      if (current.mode !== 'swipe' || !current.intent) return;
      const intent = current.intent;
      const done = commits(current.dx, viewportWidth(), event.timeStamp - current.at);
      if (current.pane) {
        // Nur zu Ende führen, wenn es dort auch etwas gibt — sonst zurück.
        current.pane.finish(done && current.pane.has(current.dx < 0 ? 1 : -1));
        return;
      }
      settle(current.moved);
      markRow(current.row, null);
      if (!done) return;
      if (intent.kind === 'back') backs.current.at(-1)?.();
      else if (intent.kind === 'row') {
        const row = current.row ? rows.current.get(current.row) : undefined;
        row?.[intent.side]?.();
      }
    };

    const onCancel = () => {
      if (gesture?.mode === 'pull') setPull(null);
      if (gesture?.mode === 'swipe') {
        if (gesture.pane) gesture.pane.finish(false);
        else settle(gesture.moved);
        markRow(gesture.row, null);
      }
      gesture = null;
    };

    root.addEventListener('touchstart', onStart, { passive: true });
    root.addEventListener('touchmove', onMove, { passive: false });
    root.addEventListener('touchend', onEnd);
    root.addEventListener('touchcancel', onCancel);
    return () => {
      root.removeEventListener('touchstart', onStart);
      root.removeEventListener('touchmove', onMove);
      root.removeEventListener('touchend', onEnd);
      root.removeEventListener('touchcancel', onCancel);
    };
  }, [contentRef, startRefresh]);

  return (
    <SwipeContext.Provider value={api}>
      {/* `overflow-x: clip`: Verschobener Inhalt darf die Seite nie breiter
          machen — sonst scrollt sie seitlich oder zoomt heraus. `clip` statt
          `hidden`, damit die klebende Kopfzeile kleben bleibt. */}
      <div ref={rootRef} className="flex min-h-dvh flex-col overflow-x-clip">
        {pull ? (
          <div
            className="pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top)+4.75rem)] z-30 flex justify-center"
            style={{
              transform: `translateY(${pull.phase === 'pulling' ? pull.px - PULL_TRIGGER_PX : 0}px)`,
            }}
          >
            <span role="status" aria-live="polite" className="kw-chip kw-glass">
              {pull.phase === 'pulling'
                ? pull.px >= PULL_TRIGGER_PX
                  ? t('gestures.pullRelease')
                  : t('gestures.pullHint')
                : pull.phase === 'syncing'
                  ? t('sync.syncing')
                  : pull.text}
            </span>
          </div>
        ) : null}
        {children}
        {undo ? (
          <div
            key={undo.key}
            role="status"
            aria-live="polite"
            className="kw-glass fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+6.5rem)] z-30 mx-auto flex max-w-lg items-center justify-between gap-3 rounded-[1.25rem] px-4 py-2"
          >
            <span className="min-w-0 truncate text-sm">{undo.message}</span>
            <button
              type="button"
              onClick={() => {
                undo.run();
                setUndo(null);
              }}
              className="kw-btn kw-btn-small shrink-0 px-3"
            >
              {t('gestures.undo')}
            </button>
          </div>
        ) : null}
      </div>
    </SwipeContext.Provider>
  );
}

/**
 * Breite des Bildschirms — am Dokument gemessen, nicht am Fenster: Ragt
 * verschobener Inhalt über den Rand, wächst `innerWidth` mit (am 24.10.2026
 * im Browser gemessen: 451 statt 375 px), und die rechte Randzone läge
 * außerhalb des Bildes.
 */
function viewportWidth(): number {
  return document.documentElement.clientWidth || window.innerWidth;
}

function useSwipeApi(): SwipeApi | null {
  return useContext(SwipeContext);
}

/**
 * Eine Unteransicht meldet „zurück“ an: Vom linken Rand nach rechts wischen
 * ruft es auf — wie der Zurück-Knopf, der bleibt.
 */
export function useSwipeBack(onBack: (() => void) | null): void {
  const api = useSwipeApi();
  const latest = useRef(onBack);
  latest.current = onBack;
  const active = onBack !== null;
  useEffect(() => {
    if (!api || !active) return;
    return api.registerBack(() => latest.current?.());
  }, [api, active]);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Am Ende der Liste gibt die Seite nur zäh nach — man spürt, dass dort nichts ist. */
const RESIST = 0.25;
const SLIDE_MS = 220;
/**
 * Abstand zwischen zwei Seiten beim Wischen — derselbe wie der Rand links und
 * rechts (zweimal 12 px), damit sie nicht aneinanderkleben (Timo, 24.10.2026).
 * Muss zu `.kw-pages` in `index.css` passen.
 */
const PAGE_GAP = 24;

/** Nach Ende einer Übergangsanimation aufräumen — auch wenn kein `transitionend` kommt. */
function afterSlide(element: HTMLElement, done: () => void): void {
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    element.removeEventListener('transitionend', finish);
    done();
  };
  element.addEventListener('transitionend', finish);
  setTimeout(finish, SLIDE_MS + 80);
}

/**
 * Seiten nebeneinander (24.10.2026): die Unterreiter oben auf einer Seite
 * (`mode="tabs"`, Tag · Woche · Bibliothek) oder Tage (`mode="pager"`).
 * Beim Wischen liegt die Nachbarseite gleich daneben und schiebt sich mit
 * herein; beim Loslassen läuft die Bewegung zu Ende. Die hereingeschobene
 * Seite behält ihren Schlüssel und wird danach nicht neu aufgebaut — ein
 * angefangener Bildlauf oder eine Eingabe bleibt.
 *
 * Die Leiste unten wird nie gewischt; im Training während der Sätze gibt es
 * keine Gesten (`enabled`).
 */
export function SwipePages<K extends string>({
  current,
  neighbour,
  onChange,
  render,
  mode = 'tabs',
  enabled = true,
}: {
  current: K;
  /** Der Nachbar in Richtung `dir` (1 = nächster, −1 = vorheriger) — `null` am Ende. */
  neighbour: (key: K, dir: Direction) => K | null;
  onChange: (key: K) => void;
  render: (key: K) => ReactNode;
  mode?: 'tabs' | 'pager';
  enabled?: boolean;
}): React.JSX.Element {
  const api = useSwipeApi();
  const outer = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const [peer, setPeer] = useState<{ key: K; dir: Direction } | null>(null);
  const peerRef = useRef(peer);
  const latest = useRef({ current, neighbour, onChange });
  latest.current = { current, neighbour, onChange };

  const controller = useMemo<PaneController>(() => {
    const showPeer = (next: { key: K; dir: Direction } | null) => {
      peerRef.current = next;
      setPeer(next);
    };
    return {
      has: (dir) => latest.current.neighbour(latest.current.current, dir) !== null,
      drag: (dx) => {
        const element = track.current;
        if (!element || prefersReducedMotion()) return;
        const dir: Direction = dx < 0 ? 1 : -1;
        const key = latest.current.neighbour(latest.current.current, dir);
        if (key !== null && (peerRef.current?.key !== key || peerRef.current.dir !== dir)) {
          showPeer({ key, dir });
        }
        element.style.transition = 'none';
        element.style.transform = `translateX(${key === null ? dx * RESIST : dx}px)`;
      },
      finish: (commit) => {
        const element = track.current;
        const target = peerRef.current;
        if (!element) return;
        if (commit && target && prefersReducedMotion()) {
          latest.current.onChange(target.key);
          showPeer(null);
          return;
        }
        if (!commit || !target) {
          element.style.transition = `transform ${SLIDE_MS}ms ease-out`;
          element.style.transform = '';
          afterSlide(element, () => {
            element.style.transition = '';
            showPeer(null);
          });
          return;
        }
        const width = (track.current?.clientWidth ?? viewportWidth()) + PAGE_GAP;
        element.style.transition = `transform ${SLIDE_MS}ms ease-out`;
        element.style.transform = `translateX(${-target.dir * width}px)`;
        afterSlide(element, () => {
          // In einem Zug: Die hereingeschobene Seite wird die aktuelle, die
          // Leiste springt zurück — kein Bild dazwischen, kein Neuaufbau.
          flushSync(() => {
            latest.current.onChange(target.key);
            peerRef.current = null;
            setPeer(null);
          });
          element.style.transition = 'none';
          element.style.transform = '';
        });
      },
    };
  }, []);

  useEffect(() => {
    if (!api || !enabled) return;
    if (mode === 'tabs') return api.registerTabs(controller);
    const element = outer.current;
    return element ? api.registerPager(element, controller) : undefined;
  }, [api, enabled, mode, controller]);

  // Wechselt die Seite von außen (Knopf oben, Pfeil), ist ein halber Wisch hinfällig.
  useEffect(() => {
    peerRef.current = null;
    setPeer(null);
    if (track.current) track.current.style.transform = '';
  }, [current]);

  return (
    <div ref={outer} className="kw-pages">
      <div ref={track} className="kw-pages-track">
        <div key={current} className="kw-page">
          {render(current)}
        </div>
        {peer && peer.key !== current ? (
          <div
            key={peer.key}
            className="kw-page pointer-events-none"
            aria-hidden="true"
            style={{ transform: `translateX(calc(${peer.dir * 100}% + ${peer.dir * PAGE_GAP}px))` }}
          >
            {render(peer.key)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Nachbar in einer festen Liste — am Ende `null`. */
export function neighbourIn<K>(keys: readonly K[]): (key: K, dir: Direction) => K | null {
  return (key, dir) => {
    const index = keys.indexOf(key);
    return index < 0 ? null : (keys[index + dir] ?? null);
  };
}

/**
 * Ein Bereich, dessen ganze Ansicht am Zeitraum hängt (Kalender, Monat,
 * Kosten): Die Nachbarwoche live mitzuzeichnen wäre teuer. Er folgt dem
 * Finger, gleitet beim Loslassen hinaus, und der neue Zeitraum gleitet von
 * der richtigen Seite herein. Gibt eine Callback-Ref zurück: Der Bereich darf
 * erst später erscheinen.
 */
export function useSwipePager<T extends HTMLElement>(
  onPrev: () => void,
  onNext: () => void,
  canNext: () => boolean = () => true,
): (element: T | null) => void {
  const api = useSwipeApi();
  const latest = useRef({ onPrev, onNext, canNext });
  latest.current = { onPrev, onNext, canNext };
  const release = useRef<(() => void) | null>(null);
  return useCallback(
    (element: T | null) => {
      release.current?.();
      release.current = null;
      if (!api || !element) return;
      let lastDx = 0;
      const has = (dir: Direction) => dir === -1 || latest.current.canNext();
      release.current = api.registerPager(element, {
        has,
        drag: (dx) => {
          lastDx = dx;
          if (prefersReducedMotion()) return;
          element.style.transition = 'none';
          element.style.transform = `translateX(${has(dx < 0 ? 1 : -1) ? dx : dx * RESIST}px)`;
        },
        finish: (commit) => {
          const dir: Direction = lastDx < 0 ? 1 : -1;
          const turn = () => (dir === 1 ? latest.current.onNext() : latest.current.onPrev());
          if (commit && prefersReducedMotion()) {
            turn();
            return;
          }
          if (!commit) {
            element.style.transition = `transform ${SLIDE_MS}ms ease-out`;
            element.style.transform = '';
            afterSlide(element, () => (element.style.transition = ''));
            return;
          }
          const width = element.clientWidth || viewportWidth();
          element.style.transition = `transform ${SLIDE_MS / 2}ms ease-in, opacity ${SLIDE_MS / 2}ms`;
          element.style.transform = `translateX(${-dir * width}px)`;
          element.style.opacity = '0';
          setTimeout(() => {
            flushSync(turn);
            // Von der anderen Seite herein.
            element.style.transition = 'none';
            element.style.transform = `translateX(${dir * width * 0.5}px)`;
            void element.offsetWidth;
            element.style.transition = `transform ${SLIDE_MS}ms ease-out, opacity ${SLIDE_MS}ms`;
            element.style.transform = '';
            element.style.opacity = '';
            afterSlide(element, () => (element.style.transition = ''));
          }, SLIDE_MS / 2);
        },
      });
    },
    [api],
  );
}

/** Leiste „Rückgängig“ — außerhalb der Gesten ein leerer Aufruf. */
export function useUndo(): (message: string, undo: () => void) => void {
  const api = useSwipeApi();
  return useCallback((message, undo) => api?.showUndo(message, undo), [api]);
}

export interface SwipeAction {
  label: string;
  tone: 'good' | 'danger' | 'accent';
  run: () => void;
}

/**
 * Eine Zeile mit Wischaktion. Beim Ziehen zeigt der Hintergrund Wort und
 * Farbe der Aktion — Vibration gibt es für Web-Apps auf iOS nicht. Der Knopf
 * in der Zeile bleibt der eigentliche Weg.
 */
export function SwipeRow({
  as: Tag = 'li',
  left,
  right,
  className = '',
  children,
}: {
  as?: 'li' | 'div';
  left?: SwipeAction | null;
  right?: SwipeAction | null;
  className?: string;
  children: ReactNode;
}): React.JSX.Element {
  const api = useSwipeApi();
  const ref = useRef<HTMLElement | null>(null);
  const latest = useRef({ left, right });
  latest.current = { left, right };
  const hasLeft = !!left;
  const hasRight = !!right;
  useEffect(() => {
    const element = ref.current;
    if (!api || !element || (!hasLeft && !hasRight)) return;
    return api.registerRow(element, {
      ...(hasLeft ? { left: () => latest.current.left?.run() } : {}),
      ...(hasRight ? { right: () => latest.current.right?.run() } : {}),
    });
  }, [api, hasLeft, hasRight]);

  return (
    <Tag
      ref={(element: HTMLElement | null) => {
        ref.current = element;
      }}
      className="kw-swipe-row"
      data-swipe-right={right ? right.label : undefined}
      data-swipe-left={left ? left.label : undefined}
      data-tone-right={right?.tone}
      data-tone-left={left?.tone}
    >
      <div data-swipe-content className={`kw-swipe-content ${className}`}>
        {children}
      </div>
    </Tag>
  );
}

/**
 * Ein Blatt nach unten wegwischen (24.10.2026) — wie „Abbrechen“. Mit
 * ungespeicherten Änderungen schnappt es zurück: Wischen soll nie still
 * etwas verwerfen.
 */
export function useSheetDismiss<T extends HTMLElement>(
  onClose: () => void,
  dirty: boolean,
): RefObject<T> {
  const ref = useRef<T>(null);
  const latest = useRef({ onClose, dirty });
  latest.current = { onClose, dirty };
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let start: { y: number; x: number } | null = null;
    let down = 0;
    const onStart = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (event.touches.length !== 1 || target.closest('input, textarea, select')) return;
      start = { x: event.touches[0]!.clientX, y: event.touches[0]!.clientY };
      down = 0;
    };
    const onMove = (event: TouchEvent) => {
      if (!start) return;
      const dx = event.touches[0]!.clientX - start.x;
      const dy = event.touches[0]!.clientY - start.y;
      const lock = lockDirection(dx, dy);
      if (!lock) return;
      if (lock === 'h' || dy < 0) {
        start = null;
        element.style.transform = '';
        return;
      }
      down = dy;
      element.style.transition = 'none';
      element.style.transform = `translateY(${latest.current.dirty ? damp(dy) * 0.3 : dy}px)`;
    };
    const onEnd = () => {
      if (!start) return;
      start = null;
      element.style.transition = 'transform 180ms ease-out';
      element.style.transform = '';
      if (down >= 96 && !latest.current.dirty) latest.current.onClose();
    };
    element.addEventListener('touchstart', onStart, { passive: true });
    element.addEventListener('touchmove', onMove, { passive: true });
    element.addEventListener('touchend', onEnd);
    element.addEventListener('touchcancel', onEnd);
    return () => {
      element.removeEventListener('touchstart', onStart);
      element.removeEventListener('touchmove', onMove);
      element.removeEventListener('touchend', onEnd);
      element.removeEventListener('touchcancel', onEnd);
    };
  }, []);
  return ref;
}

/**
 * Hochwischen auf genau diesem Element (Räume, 27.10.2026: der Raum-Knopf) —
 * die Abkürzung für sein Antippen. Gelesen wird nur auf dem Element selbst;
 * die Leiste als Ganzes bleibt ohne Geste (`data-no-swipe`). Nach dem Wischen
 * wird das folgende Antippen geschluckt, sonst öffnete es doppelt.
 */
export function useSwipeUp<T extends HTMLElement>(onUp: () => void): RefObject<T> {
  const ref = useRef<T>(null);
  const latest = useRef(onUp);
  latest.current = onUp;
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let start: { x: number; y: number } | null = null;
    let fired = false;
    let firedAt = 0;
    const onStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        start = null;
        return;
      }
      start = { x: event.touches[0]!.clientX, y: event.touches[0]!.clientY };
      fired = false;
    };
    const onMove = (event: TouchEvent) => {
      if (!start || fired) return;
      const touch = event.touches[0]!;
      if (
        swipeUpOpens(start.y, touch.clientX - start.x, touch.clientY - start.y, viewportHeight())
      ) {
        fired = true;
        firedAt = Date.now();
        latest.current();
      }
    };
    const onEnd = () => {
      start = null;
    };
    const onClick = (event: MouseEvent) => {
      // Nur das Antippen direkt nach dem Wischen — eine spätere Maus oder
      // Tastatur öffnet wie immer.
      if (!fired || Date.now() - firedAt > 600) return;
      fired = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    element.addEventListener('touchstart', onStart, { passive: true });
    element.addEventListener('touchmove', onMove, { passive: true });
    element.addEventListener('touchend', onEnd);
    element.addEventListener('touchcancel', onEnd);
    element.addEventListener('click', onClick, true);
    return () => {
      element.removeEventListener('touchstart', onStart);
      element.removeEventListener('touchmove', onMove);
      element.removeEventListener('touchend', onEnd);
      element.removeEventListener('touchcancel', onEnd);
      element.removeEventListener('click', onClick, true);
    };
  }, []);
  return ref;
}

function viewportHeight(): number {
  return window.innerHeight;
}
